// Session selector — a small dropdown anchored to the header's session
// button. Lists sessions newest-first, highlights the active one, lets the
// user switch / delete / start a new chat.
//
// LEGACY TOP-LEVEL SLOT, kept for the published component surface
// (assistantSimone.js re-export): the canonical Chat header now inlines its
// own Sessions menu (Chat.js Header.Title.Sessions). Restyled 2026-08-12 onto
// THE AI menu system (AiMenu.js) so a host that still mounts this slot gets
// the same panel/row/heading language as every other AI dropdown: the
// AiMenuPanel glass surface + motion (isOpen tracks the same
// root.showSessionMenu flag hosts already gate on), AiMenuHeading caption,
// AiOptionRow metrics on the rows with the house `selected` wash for the
// active session.

// THE house keyboard model for menus (../../functions/menuKeyboard.js) —
// held as scope VALUES on the export below, never read as bare identifiers
// from inside a factory (FA204: a frank to-json lift drops those).
import {
  menuArrowNav,
  menuFocusOnOpen,
  menuNodeVisible,
  restoreMenuFocus
} from '../../functions/menuKeyboard.js'

const Row = {
  tag: 'button',
  type: 'button',
  // Arrow-cycle marker + "this is the open thread" (the same fact the
  // `selected` wash carries visually).
  role: 'menuitem',
  ariaCurrent: (el, s) => (s.id === s.root.activeSessionId ? 'true' : null),
  flow: 'x',
  gap: 'Z',
  align: 'center space-between',
  width: '100%',
  padding: 'Y Z1',
  round: 'radiusControl',
  theme: 'transparent',
  border: 'none',
  cursor: 'pointer',
  fontSize: 'Z1',
  textAlign: 'left',
  '@coarse': { minHeight: 'controlH' },
  // Active = the house `selected` wash + `title` ink (one recipe with the
  // option rows). No `transition` on the reactive background — the runtime
  // re-applies it per tick and would restart the tween forever
  // (MenuPanel.js note).
  background: (el, s) => (s.id === s.root.activeSessionId ? 'selected' : 'transparent'),
  ':hover': { background: 'hover' },

  // The thread's name — primary text, never cut (the overflow law,
  // HOME_AMBIENT_SKIN_SPEC §3b rule 8): it wraps, a long word breaks
  // anywhere, and the time keeps its place beside it.
  Title: {
    text: (el, s) => s.title || el.call('polyglot', 'ai.chat.newChat'),
    color: (el, s) => (s.id === s.root.activeSessionId ? 'title' : 'caption'),
    fontWeight: (el, s) => (s.id === s.root.activeSessionId ? '700' : '500'),
    overflowWrap: 'anywhere',
    flex: '1 1 auto',
    minWidth: '0'
  },

  Time: {
    text: (el, s) => {
      if (!s.ts) return ''
      const d = new Date(s.ts)
      return el.call('localizedDate', d, { hour: 'numeric', minute: '2-digit' })
    },
    color: 'caption',
    flexShrink: '0',
    fontSize: 'Y'
  },

  Delete: {
    tag: 'span',
    text: '×',
    color: 'caption',
    flexShrink: '0',
    padding: 'Y2 Y',
    round: 'radiusControl',
    role: 'button',
    tabindex: '0',
    'aria-label': (el) => el.call('polyglot', 'ai.sessions.delete'),
    ':hover': { background: 'hover', color: 'title' },
    onClick: (e, el, s) => {
      e.stopPropagation()
      el.call('deleteSession', s.id)
    },
    onKeydown: (e, el, s) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        e.stopPropagation()
        el.call('deleteSession', s.id)
      }
    }
  },

  onClick: (e, el, s) => el.call('switchSession', s.id)
}

export const SessionMenu = {
  // THE shared AiMenuPanel: glass surface, radiusCard, borderless, 60vh
  // scroll cap, house open/close motion. Hosts historically mount this slot
  // gated on root.showSessionMenu — the isOpen predicate reads the same
  // flag, so an always-mounted host animates open/closed and an if:-gated
  // host simply renders open.
  extends: 'AiMenuPanel',
  // Same keyboard contract as the canonical header menu (Chat.js Sessions):
  // one role="menu" over the whole panel, arrows cycle "+ New" and every
  // session row, Enter/Space activate natively (<button> rows), Escape
  // closes and hands focus back to whatever opened the menu. Hosts gate this
  // slot on `root.showSessionMenu`, so that flag is also the focus edge.
  scope: {
    navRows: menuArrowNav,
    focusOnOpen: menuFocusOnOpen,
    nodeVisible: menuNodeVisible,
    focusBack: restoreMenuFocus
  },
  role: 'menu',
  tabindex: '-1',
  ariaLabel: (el) => el.call('polyglot', 'ai.sessions.title'),
  subscribeTo: ['showSessionMenu'],
  onRender: (el, s) =>
    el.scope.focusOnOpen(el, !!s.root.showSessionMenu, {
      when: (node) => el.scope.nodeVisible(node)
    }),
  onUpdate: (el, s) =>
    el.scope.focusOnOpen(el, !!s.root.showSessionMenu, {
      when: (node) => el.scope.nodeVisible(node)
    }),
  onKeydown: (e, el, s) => {
    if (el.scope.navRows(e, el.node)) return
    if (e.key === 'Escape') {
      e.preventDefault()
      e.stopPropagation()
      if (s.root && s.root.update) s.root.update({ showSessionMenu: false })
      el.scope.focusBack(el)
      return
    }
    if (e.key === 'Tab') {
      el._menuReturnFocusTo = null
      if (s.root && s.root.showSessionMenu) s.root.update({ showSessionMenu: false })
    }
  },
  isOpen: (el, s) => !!s.root.showSessionMenu,
  position: 'fixed',
  top: 'C1',
  right: 'A',
  zIndex: '20',
  gap: 'V',

  Header: {
    flow: 'x',
    gap: 'Y',
    align: 'center space-between',
    // Horizontal 0 — the heading self-insets to the rows' Z1 text edge
    // (AiMenuHeading paddingLeft) and the button's right edge aligns with
    // the rows' right edge.
    padding: 'Y 0',

    Label: {
      extends: 'AiMenuHeading',
      text: '{{ ai.sessions.title | polyglot }}'
    },

    NewBtn: {
      tag: 'button',
      type: 'button',
      // First stop in the panel's arrow cycle (DOM order).
      role: 'menuitem',
      text: '{{ ai.sessions.new | polyglot }}',
      padding: 'Y2 Z',
      round: 'radiusControl',
      theme: 'tertiary',
      border: 'none',
      cursor: 'pointer',
      color: 'title',
      fontSize: 'Z1',
      fontWeight: '700',
      ':hover': { background: 'hover' },
      onClick: (e, el, s) => el.call('newSession')
    }
  },

  List: {
    flow: 'y',
    gap: 'V',
    childExtends: Row,
    children: (el, s) => {
      const sess = s.root.sessions || {}
      return Object.values(sess).sort((a, b) => (b.ts || 0) - (a.ts || 0))
    },
    childrenAs: 'state',

    Empty: {
      if: (el, s) => !Object.keys(s.root.sessions || {}).length,
      padding: 'Y Z1',
      text: '{{ ai.sessions.empty | polyglot }}',
      color: 'caption',
      fontSize: 'Z1'
    }
  }
}
